import type { ControlBridgeQueueItem, ControlBridgeViewModel } from '../deriveViewModel';
import { exoActionLabel, exoClientDisplayName } from '../exoPanel/exoPanelHelpers';
import { entropyBarClass, healthMeaningFromEntropy } from '../entropyLanguage';
import { isP0, sortByPriority } from '../queueHelpers';
import { statusPillLabelFromPhrase } from '../../../utils/statusPhrasePlane';

export function statusPillLabel(statusPhrase?: string): string {
  return statusPillLabelFromPhrase(statusPhrase);
}

export function healthBarFillPct(entropy: number | null): number {
  if (entropy == null || Number.isNaN(entropy)) return 0;
  const clamped = Math.max(0, Math.min(9, entropy));
  return Math.round(((9 - clamped) / 9) * 100);
}

export function numericRollup(value: string | number | boolean | null | undefined): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export interface WaypointProgressModel {
  open: number;
  total: number;
  closed: number;
  eligibleNow: number;
  blocked: number;
  closedPct: number;
}

export function buildWaypointProgress(vm: ControlBridgeViewModel): WaypointProgressModel | null {
  const open = numericRollup(vm.rollup.waypoints);
  const total = numericRollup(vm.rollup.waypointsTotal);
  if (open == null || total == null || total <= 0) return null;
  const closed = Math.max(0, total - open);
  const eligibleNow = vm.waypoints.filter((w) => /active|in.?progress|open/i.test(w.statusPhrase)).length;
  const blocked = vm.blockedActivityCount;
  return {
    open,
    total,
    closed,
    eligibleNow,
    blocked,
    closedPct: Math.round((closed / total) * 100),
  };
}

export interface ActiveWorkModel {
  active: number;
  pool: number;
  activePct: number;
  openIssues: number;
}

export function buildActiveWorkModel(vm: ControlBridgeViewModel): ActiveWorkModel | null {
  const active = numericRollup(vm.rollup.activeActivities);
  const pool = numericRollup(vm.rollup.openImprovements);
  const openIssues = numericRollup(vm.rollup.openIssues) ?? 0;
  if (active == null || pool == null || pool <= 0) return null;
  return {
    active,
    pool,
    activePct: Math.round((active / pool) * 100),
    openIssues,
  };
}

export function pickBlockingWorkItem(
  focus: ControlBridgeQueueItem[],
  issues: ControlBridgeQueueItem[]
): ControlBridgeQueueItem | null {
  const sortedFocus = sortByPriority(focus);
  const p0 = sortedFocus.find((item) => isP0(item.priority));
  if (p0) return p0;
  if (sortedFocus[0]) return sortedFocus[0];
  const sortedIssues = sortByPriority(issues);
  return sortedIssues[0] ?? null;
}

export function healthDrivingConstraint(
  vm: ControlBridgeViewModel,
  focus: ControlBridgeQueueItem[]
): string {
  const blocker = pickBlockingWorkItem(focus, vm.openIssues);
  if (blocker?.title) {
    const title = exoClientDisplayName(blocker.title);
    if (title) {
      return `Until ${title} clears, this domain stays constrained`;
    }
  }
  if (vm.blockedActivityCount > 0) {
    return `${vm.blockedActivityCount} activit${vm.blockedActivityCount === 1 ? 'y' : 'ies'} blocked on prerequisites`;
  }
  return 'No named constraint in scope';
}

export function bridgeObjectiveGate(
  vm: ControlBridgeViewModel,
  focus: ControlBridgeQueueItem[]
): { title: string; subtitle: string } {
  const sorted = sortByPriority(focus);
  const gate = sorted.find((item) => isP0(item.priority)) ?? sorted[0];
  if (gate) {
    const parts: string[] = [];
    if (isP0(gate.priority)) parts.push('Go-live gate');
    if (gate.improvementTitle) parts.push(gate.improvementTitle);
    return {
      title: gate.title,
      subtitle: parts.length > 0 ? parts.join(' · ') : 'Recommended focus for the session',
    };
  }
  const session = typeof vm.rollup.bridgeSession === 'string' ? vm.rollup.bridgeSession.trim() : '';
  return {
    title: session || 'Bridge session',
    subtitle: 'Session objective not yet prioritized in eligible work',
  };
}

export function focusWhyNumberOne(item: ControlBridgeQueueItem): string {
  if (item.improvementTitle) {
    return `Unblocks ${item.improvementTitle}`;
  }
  if (item.activityTargetState) {
    const verb = exoActionLabel(item).toLowerCase();
    return `#1 because it ${verb} the current gate`;
  }
  if (isP0(item.priority)) {
    return 'Highest-priority eligible work in the session';
  }
  return 'Recommended as the next action in eligible work';
}

export function focusActionVerb(item: ControlBridgeQueueItem): string {
  return exoActionLabel(item);
}

export interface GlanceTileFraming {
  more: string;
  blocking: string;
  where: string;
  blockingItemId?: string;
}

const MC_DOMAIN_SLUGS = new Set(['business', 'stakeholder', 'customer']);

function glanceLocationLabel(value?: string): string | undefined {
  const v = exoClientDisplayName(value);
  if (!v) return undefined;
  if (MC_DOMAIN_SLUGS.has(v.toLowerCase())) return undefined;
  return v;
}

export function healthTileFraming(
  vm: ControlBridgeViewModel,
  focus: ControlBridgeQueueItem[]
): GlanceTileFraming {
  const worstScope = vm.scopeHealthQueue
    .filter((row) => row.correctionFactor != null)
    .sort((a, b) => (a.correctionFactor ?? 1) - (b.correctionFactor ?? 1))[0];
  return {
    more: 'Resolve the driving constraint and clear blocked prerequisites',
    blocking: healthDrivingConstraint(vm, focus),
    where: worstScope?.title ?? 'Company-wide health rollup',
  };
}

/** Compact MC domain tiles — More / Blocking / Where without raw VGR / Fair words (U5). */
export function domainGlanceFraming(
  vm: ControlBridgeViewModel,
  domain: string,
  tileTitle: string
): GlanceTileFraming {
  const matchesDomain = (item: ControlBridgeQueueItem) => {
    const hay = `${item.domain ?? ''} ${item.filterScope ?? ''} ${item.title ?? ''}`.toLowerCase();
    return hay.includes(domain.toLowerCase());
  };
  const focus = [
    ...vm.recommendedFocus.filter(matchesDomain),
    ...vm.eligibleWork.filter(matchesDomain),
    ...vm.blockedWork.filter(matchesDomain),
  ];
  const scopedFocus = focus.length ? focus : vm.recommendedFocus;
  const scopedIssues = vm.openIssues.filter(matchesDomain);
  const framing = healthTileFraming(
    {
      ...vm,
      openIssues: scopedIssues.length ? scopedIssues : vm.openIssues,
      blockedActivityCount: vm.blockedWork.filter(matchesDomain).length || vm.blockedActivityCount,
    },
    scopedFocus
  );
  const lever = pickBlockingWorkItem(scopedFocus, scopedIssues);
  const leverTitle = lever ? exoClientDisplayName(lever.title) : null;
  const more = leverTitle
    ? `Clear the constraint: ${leverTitle}`
    : 'No ranked lever in this domain yet';
  const where =
    glanceLocationLabel(lever?.improvementTitle) ||
    glanceLocationLabel(lever?.filterScope) ||
    (framing.where && framing.where !== 'Company-wide health rollup'
      ? glanceLocationLabel(framing.where)
      : undefined) ||
    tileTitle;
  return {
    more,
    blocking: framing.blocking,
    where,
    blockingItemId: lever?.itemId,
  };
}

export function waypointTileFraming(vm: ControlBridgeViewModel, progress: WaypointProgressModel): GlanceTileFraming {
  const activeWp = vm.waypoints.find((w) => /active|in.?progress/i.test(w.statusPhrase));
  return {
    more: 'Close the active waypoint to advance the session pipeline',
    blocking:
      progress.blocked > 0
        ? `${progress.blocked} activit${progress.blocked === 1 ? 'y' : 'ies'} blocked on prerequisites`
        : progress.eligibleNow > 0
          ? `${progress.eligibleNow} waypoint${progress.eligibleNow === 1 ? '' : 's'} eligible now`
          : 'No waypoint eligible until prerequisites clear',
    where: activeWp?.title ?? 'Session waypoint map',
  };
}

export function activeWorkTileFraming(
  vm: ControlBridgeViewModel,
  focus: ControlBridgeQueueItem[],
  work: ActiveWorkModel
): GlanceTileFraming {
  const blocker = pickBlockingWorkItem(focus, vm.openIssues);
  return {
    more: 'Move the next eligible activity to completion',
    blocking: blocker?.title ?? (work.openIssues > 0 ? `${work.openIssues} open issue${work.openIssues === 1 ? '' : 's'}` : 'No named blocker'),
    where: blocker?.domain ?? blocker?.filterScope ?? 'Eligible work queue',
  };
}

export function contextualNavHints(vm: ControlBridgeViewModel): Record<string, string> {
  const moving = vm.eligibleWork.filter((item) => /active|in.?progress|ready/i.test(item.statusPhrase)).length;
  const waypointCount = vm.waypoints.filter((w) => /active|open|in.?progress/i.test(w.statusPhrase)).length;
  const issueCount = vm.openIssues.length;
  const focusCount = vm.recommendedFocus.length;
  return {
    mission: focusCount > 0 ? `${focusCount} focus item${focusCount === 1 ? '' : 's'}` : 'Where to focus',
    drive: moving > 0 ? `${moving} item${moving === 1 ? '' : 's'} moving` : 'No active flow',
    guidance: waypointCount > 0 ? `${waypointCount} waypoint${waypointCount === 1 ? '' : 's'} in plan` : 'Plan view',
    diagnose: issueCount > 0 ? `${issueCount} open issue${issueCount === 1 ? '' : 's'}` : 'Health view',
    issues: 'Filed issues inbox',
    exopanel: focusCount > 0 ? `${focusCount} ready to act` : 'Execution queue',
  };
}

export function healthBarTone(entropy: number | null): 'good' | 'mid' | 'weak' | 'muted' {
  return entropyBarClass(entropy);
}

export function healthMeaning(entropy: number | null): 'good' | 'warn' | 'alert' | 'muted' {
  return healthMeaningFromEntropy(entropy);
}
